/**
 * Users: who works in BlueSphere MDR and what each may do (Story 22, task
 * 116, ADR-0018), by organization (Story 48, task 208).
 *
 * The records homed at an organization — by default the caller's own, with
 * the organizations beneath it — disabled ones included, with role, home,
 * scope, and whether each has bound to a Microsoft sign-in yet. Invite,
 * edit role, home and scope, enable or disable, and each user's audit
 * trail. All of it is `manage_org`, which an Admin with scope "this
 * organization and below" holds for their organization and below; the nav
 * entry is hidden from everyone else and the API refuses them regardless.
 * Email settings moved to Preferences (task 208).
 *
 * The server owns the rules and this page shows its refusals inline: 409
 * `email_exists` on invite; `last_admin` when a change would leave an
 * organization without an enabled Admin of its own; `self_disable`; `conflict` when the
 * users changed underneath the edit. The caller's own row offers no
 * Disable, and a change to it refreshes `/me` at once, because the server
 * applies the acting Admin's own change immediately.
 */

import { Fragment, useMemo, useState } from 'react';
import { useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  inviteUser,
  listTenants,
  listUserAudit,
  listUsers,
  updateUser,
  type ClientTenant,
  type InviteUserBody,
  type PlatformUser,
  type UpdateUserBody,
  type UserAuditEntry,
} from '@bluesphere/api-client';
import { PillButton } from '@bluesphere/ui-kit';

import { useAccessToken } from '../auth/useAccessToken';
import { useOrgAdmin } from '../auth/useOrgAdmin';
import { useSelectedTenant } from '../auth/useSelectedTenant';
import { useTenant } from '../auth/useTenant';
import { ConfirmModal } from '../components/ConfirmModal';
import {
  EditUserModal,
  InviteUserModal,
  InvitedModal,
  type HomeChoice,
} from '../components/UserModals';
import { EmptyState, ErrorState, Loading } from '../components/states';
import { absoluteTime, timeAgo } from '@bluesphere/mdr-alert-panel';
import { clientsUnder, depthOf, isParentKind, orgLabel } from '../lib/orgs';
import { ROLE_LABELS, scopeLabel } from '../lib/users';

/**
 * One column template for the header and every row, so the columns line up:
 * each row is its own grid, so an `auto` actions column (wider on rows that
 * offer Disable) or content-sized `fr` tracks shift the columns row by row.
 * `minmax(0, …)` stops a long value widening its track; the actions column
 * is fixed. Whole class strings, so Tailwind's scanner sees them.
 */
const COLUMNS =
  'grid-cols-[minmax(0,2fr)_minmax(0,0.8fr)_minmax(0,1.3fr)_minmax(0,1.7fr)_minmax(0,1fr)_11rem]';
const COLUMNS_MD =
  'md:grid-cols-[minmax(0,2fr)_minmax(0,0.8fr)_minmax(0,1.3fr)_minmax(0,1.7fr)_minmax(0,1fr)_11rem]';

type Editing = { user: PlatformUser; enabling: boolean };

export function Users() {
  const idToken = useAccessToken();
  const { me } = useTenant();
  const { orgs } = useSelectedTenant();
  const orgAdmin = useOrgAdmin();
  const queryClient = useQueryClient();
  const allowed = orgAdmin.holds;
  const treeMode = !orgAdmin.preTree;

  // Which organization's users (default: the caller's home), and whether
  // the organizations beneath it are included.
  const [filterOrg, setFilterOrg] = useState<string | null>(null);
  const [withChildren, setWithChildren] = useState(true);
  const org = treeMode ? (filterOrg ?? orgAdmin.homeId ?? undefined) : undefined;

  const usersQuery = useQuery({
    queryKey: ['users', org ?? 'all', withChildren],
    queryFn: async () =>
      (await listUsers(idToken, treeMode ? { org, children: withChildren } : {})).users,
    enabled: Boolean(idToken && allowed),
  });

  // The organizations the caller manages: the filter, and the home picker.
  const homes: HomeChoice[] = useMemo(() => {
    if (!treeMode || !orgAdmin.homeId) return [];
    const top = depthOf(orgs, orgAdmin.homeId);
    return orgs.ordered
      .filter((n) => isParentKind(n) && n.status === 'active' && orgAdmin.at(n.org_id))
      .map((n) => ({ id: n.org_id, label: orgLabel(n), depth: depthOf(orgs, n.org_id) - top }));
  }, [treeMode, orgs, orgAdmin]);
  const clientsOf = (homeId: string) => clientsUnder(orgs, homeId);
  const homeName = (id: string | null) => (id ? orgLabel(orgs.byId.get(id)) : '—');
  // The same list the Clients page polls; here it names scope entries and
  // feeds the scope picker.
  const tenantsQuery = useQuery({
    queryKey: ['tenants'],
    queryFn: async () => (await listTenants(idToken)).tenants,
    enabled: Boolean(idToken && allowed),
  });
  const tenants: ClientTenant[] = tenantsQuery.data ?? [];
  // Client names for scopes, and organization names for homes in the history.
  const names = useMemo(
    () =>
      new Map([
        ...orgs.ordered.map((n) => [n.org_id, orgLabel(n)] as [string, string]),
        ...tenants.map((t) => [t.client_tenant_id, t.display_name] as [string, string]),
      ]),
    [tenants, orgs],
  );

  const [inviting, setInviting] = useState(false);
  const [invited, setInvited] = useState<PlatformUser | null>(null);
  const [editing, setEditing] = useState<Editing | null>(null);
  const [disabling, setDisabling] = useState<PlatformUser | null>(null);
  const [openAudit, setOpenAudit] = useState<string | null>(null);

  const afterChange = (userId: string) => {
    void queryClient.invalidateQueries({ queryKey: ['users'] });
    if (userId === me?.user_id) void queryClient.invalidateQueries({ queryKey: ['me'] });
  };

  const inviteMutation = useMutation<PlatformUser, Error, InviteUserBody>({
    mutationFn: (body) => inviteUser(body, idToken),
    onSuccess: (user) => {
      setInviting(false);
      setInvited(user);
      afterChange(user.user_id);
    },
  });

  const updateMutation = useMutation<PlatformUser, Error, { userId: string; body: UpdateUserBody }>(
    {
      mutationFn: ({ userId, body }) => updateUser(userId, body, idToken),
      onSuccess: (user) => {
        setEditing(null);
        setDisabling(null);
        afterChange(user.user_id);
      },
    },
  );

  const setEnabled = (user: PlatformUser, enabled: boolean) => {
    if (!enabled) {
      updateMutation.reset();
      setDisabling(user);
      return;
    }
    // A disabled record may hold an empty scope; enabling it must carry one.
    if (user.scope !== 'all' && user.scope.length === 0) {
      updateMutation.reset();
      setEditing({ user, enabling: true });
      return;
    }
    updateMutation.mutate({ userId: user.user_id, body: { enabled: true } });
  };

  if (!allowed) {
    return (
      <div className="bs-card">
        <EmptyState
          title="Users are managed by an organization's Admins"
          body="Your role does not include managing users."
        />
      </div>
    );
  }

  const users = usersQuery.data ?? [];

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Users</h1>
          <p className="mt-1 text-sm text-white/60">
            {treeMode
              ? `Users of ${homeName(org ?? null)}${withChildren ? ' and the organizations beneath it' : ''}. `
              : 'Simulint staff with access to BlueSphere MDR. '}
            Role decides what they can do; scope decides which Clients. Changes reach them within a
            minute.
          </p>
        </div>
        <PillButton
          onClick={() => {
            inviteMutation.reset();
            setInviting(true);
          }}
        >
          Invite a user
        </PillButton>
      </div>

      {treeMode && homes.length > 1 && (
        <div className="flex flex-wrap items-center gap-4 text-sm text-white/80">
          <label className="flex items-center gap-2">
            Organization
            <select
              value={org ?? ''}
              onChange={(e) => setFilterOrg(e.target.value || null)}
              className="rounded-full border border-white/20 bg-white/10 px-3 py-1.5 text-sm text-white"
            >
              {homes.map((h) => (
                <option key={h.id} value={h.id} className="text-bluesphere-textOnLight">
                  {'\u00a0\u00a0'.repeat(h.depth)}
                  {h.label}
                </option>
              ))}
            </select>
          </label>
          <label className="flex cursor-pointer items-center gap-2">
            <input
              type="checkbox"
              checked={withChildren}
              onChange={(e) => setWithChildren(e.target.checked)}
              className="h-4 w-4 rounded-sm border-white/30 bg-white/10"
            />
            Include sub-organizations
          </label>
        </div>
      )}

      {/* A failed enable without a modal open has nowhere else to show. */}
      {updateMutation.error && !editing && !disabling && (
        <ErrorState title="Could not change the user" error={updateMutation.error} />
      )}

      {usersQuery.isLoading ? (
        <div className="bs-card">
          <Loading />
        </div>
      ) : usersQuery.error ? (
        <ErrorState
          title="Could not load users"
          error={usersQuery.error}
          onRetry={() => void usersQuery.refetch()}
        />
      ) : users.length === 0 ? (
        <div className="bs-card">
          <EmptyState title="No users" />
        </div>
      ) : (
        <div className="bs-card">
          <div
            className={`hidden ${COLUMNS} gap-4 border-b border-bluesphere-border bg-bluesphere-surfaceMuted px-5 py-3 text-xs uppercase tracking-wider text-bluesphere-textSecondary md:grid`}
          >
            <span>User</span>
            <span>Role</span>
            <span>Home organization</span>
            <span>Scope</span>
            <span>State</span>
            <span className="text-right">Actions</span>
          </div>
          <ul className="divide-y divide-bluesphere-border">
            {users.map((user) => (
              <Fragment key={user.user_id}>
                <UserRow
                  user={user}
                  home={treeMode ? homeName(user.home_org_id) : 'Simulint'}
                  names={names}
                  isSelf={user.user_id === me?.user_id}
                  auditOpen={openAudit === user.user_id}
                  busy={
                    updateMutation.isPending && updateMutation.variables?.userId === user.user_id
                  }
                  onEdit={() => {
                    updateMutation.reset();
                    setEditing({ user, enabling: false });
                  }}
                  onSetEnabled={(enabled) => setEnabled(user, enabled)}
                  onToggleAudit={() =>
                    setOpenAudit((open) => (open === user.user_id ? null : user.user_id))
                  }
                />
                {openAudit === user.user_id && (
                  <li className="bg-bluesphere-surfaceMuted px-5 py-4">
                    <AuditPanel userId={user.user_id} names={names} />
                  </li>
                )}
              </Fragment>
            ))}
          </ul>
        </div>
      )}

      {inviting && (
        <InviteUserModal
          tenants={tenants}
          homes={homes}
          defaultHome={org ?? null}
          clientsOf={clientsOf}
          onCancel={() => setInviting(false)}
          onSubmit={(body) => inviteMutation.mutate(body)}
          isPending={inviteMutation.isPending}
          error={inviteMutation.error}
        />
      )}
      {invited && <InvitedModal user={invited} onClose={() => setInvited(null)} />}
      {editing && (
        <EditUserModal
          user={editing.user}
          tenants={tenants}
          homes={homes}
          clientsOf={clientsOf}
          enabling={editing.enabling}
          onCancel={() => {
            setEditing(null);
            updateMutation.reset();
          }}
          onSubmit={(body) => updateMutation.mutate({ userId: editing.user.user_id, body })}
          isPending={updateMutation.isPending}
          error={updateMutation.error}
        />
      )}
      {disabling && (
        <ConfirmModal
          title={`Disable ${disabling.display_name || disabling.email}?`}
          confirmLabel="Disable"
          pendingLabel="Disabling…"
          onCancel={() => {
            setDisabling(null);
            updateMutation.reset();
          }}
          onConfirm={() =>
            updateMutation.mutate({ userId: disabling.user_id, body: { enabled: false } })
          }
          isPending={updateMutation.isPending}
          error={updateMutation.error}
        >
          <p className="text-sm text-bluesphere-textSecondary">
            Every request they make is refused within a minute, including any session already open.
            The record, its role and scope are kept; enable it again to restore access.
          </p>
        </ConfirmModal>
      )}
    </div>
  );
}

// ----- One user ----------------------------------------------------------------

function UserRow({
  user,
  home,
  names,
  isSelf,
  auditOpen,
  busy,
  onEdit,
  onSetEnabled,
  onToggleAudit,
}: {
  user: PlatformUser;
  home: string;
  names: Map<string, string>;
  isSelf: boolean;
  auditOpen: boolean;
  busy: boolean;
  onEdit: () => void;
  onSetEnabled: (enabled: boolean) => void;
  onToggleAudit: () => void;
}) {
  const link = 'text-xs text-bluesphere-textOnLight underline hover:text-bluesphere-secondary';
  const danger = 'text-xs text-red-700 underline hover:text-red-900';
  return (
    <li
      className={`grid gap-2 px-5 py-4 text-sm ${COLUMNS_MD} md:items-start md:gap-4 ${
        user.enabled ? '' : 'opacity-70'
      }`}
    >
      <div className="min-w-0">
        <div className="font-medium text-bluesphere-textOnLight">
          {user.display_name || user.email}
          {isSelf && <span className="ml-2 text-xs font-normal text-bluesphere-muted">(you)</span>}
        </div>
        <div className="break-all text-xs text-bluesphere-muted">{user.email}</div>
      </div>
      <div className="text-bluesphere-textOnLight">
        <span className="text-xs text-bluesphere-textSecondary md:hidden">Role: </span>
        {ROLE_LABELS[user.role]}
      </div>
      <div className="min-w-0 text-bluesphere-textOnLight">
        <span className="text-xs text-bluesphere-textSecondary md:hidden">Home: </span>
        {home}
      </div>
      <div className="min-w-0 text-bluesphere-textOnLight">
        <span className="text-xs text-bluesphere-textSecondary md:hidden">Scope: </span>
        {scopeLabel(user.scope, names, home)}
      </div>
      <div className="flex flex-wrap gap-1.5">
        <Badge tone={user.enabled ? 'good' : 'muted'}>
          {user.enabled ? 'Enabled' : 'Disabled'}
        </Badge>
        {!user.bound && (
          <Badge tone="pending" title="Binds at their first Microsoft sign-in">
            Pending sign-in
          </Badge>
        )}
      </div>
      <div className="flex flex-wrap items-center gap-3 md:justify-end">
        <button type="button" onClick={onEdit} className={link} disabled={busy}>
          Edit
        </button>
        {user.enabled ? (
          !isSelf && (
            <button
              type="button"
              onClick={() => onSetEnabled(false)}
              className={danger}
              disabled={busy}
            >
              Disable
            </button>
          )
        ) : (
          <button type="button" onClick={() => onSetEnabled(true)} className={link} disabled={busy}>
            {busy ? 'Enabling…' : 'Enable'}
          </button>
        )}
        <button type="button" onClick={onToggleAudit} className={link} aria-expanded={auditOpen}>
          {auditOpen ? 'Hide history' : 'History'}
        </button>
      </div>
    </li>
  );
}

function Badge({
  tone,
  title,
  children,
}: {
  tone: 'good' | 'pending' | 'muted';
  title?: string;
  children: string;
}) {
  const classes = {
    good: 'bg-emerald-100 text-emerald-800 border-emerald-300',
    pending: 'bg-amber-100 text-amber-800 border-amber-300',
    muted: 'bg-bluesphere-surfaceMuted text-bluesphere-textSecondary border-bluesphere-borderMuted',
  }[tone];
  return (
    <span
      title={title}
      className={`rounded-sm border px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider ${classes}`}
    >
      {children}
    </span>
  );
}

// ----- Audit ---------------------------------------------------------------------

const AUDIT_ACTIONS: Record<string, string> = {
  migrate: 'Moved into the organization tree',
  invite: 'Invited',
  update: 'Changed',
  bind: 'First sign-in bound the record',
  entra_oid: 'Microsoft object id recorded',
  email_preferences: 'Email preferences changed',
  email_settings: 'Email settings changed',
};

function AuditPanel({ userId, names }: { userId: string; names: Map<string, string> }) {
  const idToken = useAccessToken();
  const audit = useInfiniteQuery({
    queryKey: ['users', userId, 'audit'],
    queryFn: ({ pageParam }) => listUserAudit(userId, idToken, pageParam),
    initialPageParam: null as string | null,
    getNextPageParam: (page) => page.next_cursor,
    enabled: Boolean(idToken),
  });

  if (audit.isLoading) return <Loading height="h-16" />;
  if (audit.error) return <ErrorState title="Could not load history" error={audit.error} />;

  const entries = audit.data?.pages.flatMap((p) => p.items) ?? [];
  if (entries.length === 0) {
    return <p className="text-xs text-bluesphere-muted">No history recorded.</p>;
  }

  return (
    <div className="space-y-3">
      <ol className="space-y-3">
        {entries.map((e) => (
          <li key={e.audit_id} className="text-xs text-bluesphere-textOnLight">
            <div>
              <span className="font-semibold">{AUDIT_ACTIONS[e.action] ?? e.action}</span>
              <span className="text-bluesphere-textSecondary">
                {' '}
                by {actorLabel(e)} · <span title={absoluteTime(e.at)}>{timeAgo(e.at)}</span>
              </span>
            </div>
            {e.action !== 'invite' && Object.keys(e.changes).length > 0 && (
              <ul className="mt-1 space-y-0.5 text-bluesphere-textSecondary">
                {Object.entries(e.changes).map(([field, change]) => (
                  <li key={field}>
                    {fieldLabel(field)}: {valueLabel(field, change.before, names)} →{' '}
                    <span className="text-bluesphere-textOnLight">
                      {valueLabel(field, change.after, names)}
                    </span>
                  </li>
                ))}
              </ul>
            )}
            {e.action === 'invite' && (
              <div className="mt-1 text-bluesphere-textSecondary">
                {['role', 'scope']
                  .filter((f) => f in e.changes)
                  .map((f) => `${fieldLabel(f)}: ${valueLabel(f, e.changes[f]!.after, names)}`)
                  .join(' · ')}
              </div>
            )}
          </li>
        ))}
      </ol>
      {audit.hasNextPage && (
        <PillButton
          variant="outline"
          size="sm"
          onClick={() => void audit.fetchNextPage()}
          disabled={audit.isFetchingNextPage}
        >
          {audit.isFetchingNextPage ? 'Loading…' : 'Load more'}
        </PillButton>
      )}
    </div>
  );
}

/** A person by email; a system actor (`system:binding`, `system:migration`) as System. */
function actorLabel(e: UserAuditEntry): string {
  if (e.actor_user_id.startsWith('system:')) return `System (${e.actor_user_id.slice(7)})`;
  return e.actor_email || e.actor_user_id;
}

const FIELD_LABELS: Record<string, string> = {
  role: 'Role',
  scope: 'Scope',
  enabled: 'Enabled',
  email: 'Email',
  display_name: 'Name',
  sub: 'Sign-in',
  entra_oid: 'Microsoft object id',
  home_org_id: 'Home organization',
};

function fieldLabel(field: string): string {
  return FIELD_LABELS[field] ?? field;
}

function valueLabel(field: string, value: unknown, names: Map<string, string>): string {
  if (value === null || value === undefined) return '—';
  if (field === 'role' && typeof value === 'string' && value in ROLE_LABELS) {
    return ROLE_LABELS[value as keyof typeof ROLE_LABELS];
  }
  if (field === 'scope' && (value === 'all' || Array.isArray(value))) {
    return scopeLabel(value as 'all' | string[], names);
  }
  if (field === 'home_org_id' && typeof value === 'string') {
    return names.get(value) ?? `${value.slice(0, 8)}…`;
  }
  if (typeof value === 'boolean') return value ? 'yes' : 'no';
  return String(value);
}
